import styled from '@emotion/styled/macro';
import { Banner } from '../base/banner/banner';
import { BaseButton } from '../base/button/button';
import { UnderlineTabs } from '../base/underline-tabs/underline-tabs';
import { Variables } from '../base/variables';

const V = Variables.ThemeLight;

/** Same page frame as the Roster, so the two read as one console. */
export const Wrapper = styled.section`
    padding: 32px 40px 40px;
`;

export const PageBanner = styled(Banner)`
    margin-bottom: 16px;
`;

export const Header = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 16px;
`;

/** Separate font properties, not the shorthand — see the Roster title (PP-21). */
export const Title = styled.h1`
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.2;
    color: ${V.TextStrong};
`;

export const Tabs = styled(UnderlineTabs)`
    margin-bottom: 14px;
` as typeof UnderlineTabs;

export const TabLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;

    svg {
        fill: currentColor;
    }
`;

export const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
`;

/** A toggle option with a reason on hover — a disabled button shows no title of its own. */
export const ToggleSlot = styled.span`
    display: inline-flex;
`;

export const ToggleSub = styled.span`
    font-weight: 400;
    opacity: 0.8;
`;

/** The white card holding the table. */
export const Panel = styled.div`
    background: ${V.Background3};
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    overflow: hidden;

    /* The row whose record the drawer shows (DataTable \`rowClass\`). */
    .file-cabinet-open-row {
        background: ${V.PrimaryMild1};
        box-shadow: inset 3px 0 0 ${V.PrimaryMedium3};
    }
`;

/* ── table controls ───────────────────────────────────────────── */

export const Leading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-right: auto;
`;

/** The search box with what goes beside it: the "Press Enter" hint, and the query builder's strip. */
export const SearchBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const Search = styled.div`
    width: 320px;
    flex: 0 1 320px;
    min-width: 180px;
`;

export const RecordCount = styled.span`
    font-size: 12px;
    white-space: nowrap;
    color: ${V.TextMild};
`;

/* ── cells ────────────────────────────────────────────────────── */

export const Two = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

export const Primary = styled.span<{ strong?: boolean; mono?: boolean }>`
    font-size: 13px;
    font-weight: ${({ strong }) => (strong ? 600 : 500)};
    color: ${V.TextStrong};
    font-variant-numeric: ${({ mono }) => (mono ? 'tabular-nums' : 'normal')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Secondary = styled.span`
    font-size: 12px;
    color: ${V.TextMild};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Muted = styled.span`
    font-size: 13px;
    color: ${V.TextMedium};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** An empty cell. Faint, so a column of dashes recedes rather than reading as data. */
export const Absent = styled.span`
    font-size: 13px;
    color: ${V.TextXMild};
`;

export const Mono = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
`;

export const LineageNote = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;

    svg {
        fill: currentColor;
    }
`;

export type ChipTone = 'neutral' | 'primary' | 'success' | 'danger';

/** NS-2 pairs: a pale surface with dark text from the same family; neutral is surface + border. */
const CHIP: Record<ChipTone, { background: string; color: string; border: string }> = {
    neutral: { background: V.Background1, color: V.TextMedium, border: V.Border1 },
    primary: { background: V.PrimaryMild2, color: V.PrimaryInverted3, border: 'transparent' },
    success: { background: V.SuccessMild2, color: V.SuccessInverted3, border: 'transparent' },
    danger: { background: V.WarningMild2, color: V.WarningInverted3, border: 'transparent' },
};

export const Chip = styled.span<{ tone: ChipTone }>`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 3px 7px;
    border-radius: 4px;
    border: 1px solid ${({ tone }) => CHIP[tone].border};
    font-size: 11px;
    font-weight: 500;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: ${({ tone }) => CHIP[tone].background};
    color: ${({ tone }) => CHIP[tone].color};
`;

export const ClaimId = styled(Mono)`
    color: ${V.PrimaryInverted3};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Empty = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 48px 16px;
    text-align: center;
`;

export const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${V.TextStrong};
`;

export const EmptyHint = styled.div`
    font-size: 13px;
    color: ${V.TextMild};
`;

/* ── drawer ───────────────────────────────────────────────────── */

export const ButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

/** A postal address, one line per part. */
export const Address = styled.span`
    white-space: pre-line;
`;

export const ClaimList = styled.span`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const Preview = styled.div`
    min-height: 120px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    overflow: hidden;
    background: ${V.Background1};
`;

export const PreviewNote = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 120px;
    padding: 16px;
    font-size: 13px;
    color: ${V.TextMild};
    text-align: center;
`;

export const Timeline = styled.ol`
    margin: 0;
    padding: 0;
    list-style: none;
`;

export const TimelineItem = styled.li`
    display: flex;
    gap: 12px;
`;

export const TimelineRail = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 12px;
    flex: none;
`;

export const TimelineDot = styled.span<{ latest: boolean }>`
    width: 9px;
    height: 9px;
    margin-top: 4px;
    border-radius: 50%;
    background: ${({ latest }) => (latest ? V.PrimaryMedium3 : V.Border2)};
`;

export const TimelineLine = styled.span`
    flex: 1;
    width: 1px;
    min-height: 22px;
    background: ${V.Border1};
`;

export const TimelineBody = styled.div`
    padding-bottom: 12px;
`;

export const TimelineName = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const TimelineMeta = styled.div`
    font-size: 12px;
    color: ${V.TextMild};
`;

/** The carried-document card: the base button, unstyled, around a row that reads as a link. */
/** A row's first cell, as the button that opens its record — looks like the cell, focusable like a button. */
export const RowOpenButton = styled(BaseButton)`
    display: block;
    width: 100%;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${V.PrimaryMedium3};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

export const DocumentLinkButton = styled(BaseButton)`
    display: block;
    width: 100%;
    padding: 0;
    text-align: left;
`;

export const DocumentLink = styled.span`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
    color: ${V.TextMedium};

    &:hover {
        background: ${V.Background1};
    }

    svg {
        flex: none;
        fill: currentColor;
    }
`;

export const DocumentLinkText = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

/** "Attached to": up to three record icons and a label. */
export const AttachedList = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: ${V.TextMedium};

    svg {
        flex: none;
        fill: currentColor;
    }
`;

/** A short note with an action beside it — "could not be loaded · Retry". */
export const SectionNote = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

/** An email body: plain text, line breaks kept. */
export const MessageBody = styled.div`
    padding: 12px 14px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background1};
    font-size: 13px;
    line-height: 1.55;
    color: ${V.TextStrong};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

/** Read by screen readers, not shown — the text an icon-only cell stands for. */
export const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

/* ── full-text search: builder and strip ──────────────────────── */

export const FtsPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 560px;
    max-width: calc(100vw - 48px);
    padding: 8px 16px;
    user-select: text;
`;

export const FtsPanelTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const FtsGrid = styled.div`
    display: grid;
    grid-template-columns: 130px minmax(0, 1fr) 84px;
    gap: 8px 12px;
    align-items: center;
`;

export const FtsLabel = styled.label`
    font-size: 13px;
    color: ${V.TextMedium};
`;

export const FtsOperator = styled(Mono)`
    color: ${V.TextXMild};
    white-space: nowrap;
`;

export const FtsNear = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 13px;
    color: ${V.TextMild};

    > :first-of-type {
        flex: 1;
        min-width: 0;
    }
`;

export const FtsDistance = styled.div`
    width: 64px;
    flex: none;
`;

export const FtsHint = styled.div`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const FtsPreview = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background1};
    overflow-wrap: anywhere;
`;

/** The wire form of the query — long and unbroken, so it wraps anywhere instead of leaving the panel. */
export const FtsWire = styled(Mono)`
    font-size: 11px;
    color: ${V.TextXMild};
    overflow-wrap: anywhere;
    word-break: break-all;
`;

export const FtsPreviewLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const FtsReference = styled.dl`
    display: grid;
    grid-template-columns: 130px 1fr;
    gap: 6px 12px;
    margin: 8px 0 0;

    dt {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12px;
        color: ${V.TextStrong};
    }

    dd {
        margin: 0;
        font-size: 12px;
        color: ${V.TextMedium};
    }
`;

export const FtsFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid ${V.Border1};
`;

export const FtsStrip = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    /* In line with the table's controls row (16px padding). */
    margin: 0 16px 12px;
    padding: 10px 12px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background1};
`;

export const FtsChips = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`;

export const FtsChip = styled.span<{ ignored?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 2px 0 4px;
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    background: ${V.Background3};
    opacity: ${({ ignored }) => (ignored ? 0.6 : 1)};

    ${Mono} {
        color: ${V.TextStrong};
        text-decoration: ${({ ignored }) => (ignored ? 'line-through' : 'none')};
    }
`;

export const FtsChipRemove = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    color: ${V.TextMild};
`;

export const FtsCount = styled.span`
    margin-left: auto;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const FtsWarning = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: ${V.AttentionInverted3};

    svg {
        flex: none;
        margin-top: 1px;
    }
`;
